import type { TakeoverData } from 'bundles/search-common/experiments/takeoverPilot/TakeoverCard';
import type { TakeoverPilotConfigEntry } from 'bundles/search-common/experiments/takeoverPilot/takeoverPilotConfig';
import { getCampaignConfig } from 'bundles/search-common/experiments/takeoverPilot/takeoverPilotConfig';
import type { SearchHit, SearchUpsellHit } from 'bundles/search-common/providers/searchTypes';
import { isSearchUpsellHit } from 'bundles/search-common/utils/utils';

export type InsertModeCampaign = Extract<TakeoverPilotConfigEntry, { mode: 'insert' }>;

// campaignConfig defaults to a fresh getCampaignConfig() call, but callers in a render loop (e.g.
// SearchCards, called once per hit) should pass a config they've memoized themselves -- otherwise
// the map (and every _t() call inside it) gets rebuilt per hit on every render for nothing.
export const getInsertModeCampaign = (
  hit: SearchHit | SearchUpsellHit,
  campaignConfig: Record<string, TakeoverPilotConfigEntry> = getCampaignConfig()
): InsertModeCampaign | undefined => {
  if (!isSearchUpsellHit(hit)) {
    return undefined;
  }
  const { takeoverCampaignId } = hit;
  if (!takeoverCampaignId) {
    return undefined;
  }
  const entry = campaignConfig[takeoverCampaignId];
  return entry?.mode === 'insert' ? entry : undefined;
};

export const buildInsertModeTakeoverData = (campaign: InsertModeCampaign): TakeoverData => ({
  campaignId: campaign.campaignId,
  partnerId: campaign.partnerId,
  partnerName: campaign.partnerName,
  ctaLabel: campaign.ctaLabel,
  backgroundColor: campaign.backgroundColor,
  heroImageUrl: campaign.heroImageUrl,
  heroImageAlt: campaign.heroImageAlt,
  heroImageIsFullBleed: campaign.heroImageIsFullBleed,
  partnerLogoUrl: campaign.partnerLogoUrl,
  headline: campaign.headline,
  description: campaign.description,
});

// Headline, description, and logo come from the matched hit (sourcedContent); rest from config.
export const buildMatchModeTakeoverData = (
  campaign: TakeoverPilotConfigEntry,
  sourcedContent: { headline: string; description?: string; partnerLogoUrl?: string }
): TakeoverData => ({
  campaignId: campaign.campaignId,
  partnerId: campaign.partnerId,
  partnerName: campaign.partnerName,
  ctaLabel: campaign.ctaLabel,
  backgroundColor: campaign.backgroundColor,
  heroImageUrl: campaign.heroImageUrl,
  heroImageAlt: campaign.heroImageAlt,
  heroImageIsFullBleed: campaign.heroImageIsFullBleed,
  headline: sourcedContent.headline,
  description: sourcedContent.description,
  partnerLogoUrl: sourcedContent.partnerLogoUrl,
});
